import { useState } from "react";
import { useNavigate } from "react-router";
import dayjs from "dayjs";
import "dayjs/locale/es";

import {
  MARKET_INTELLIGENCE_FREE_ENDS_AT,
  MARKET_INTELLIGENCE_FREE_UNTIL,
  SALES_EMAIL,
} from "@/constants";
import { capture } from "@/lib/posthog";
import { useUpgradePlan } from "@/hooks/queries/useBillingPlans";
import { useMySubscription } from "@/hooks/queries/useMySubscription";
import { useViewQuota } from "@/hooks/queries/useViewQuota";
import { useActiveProfile } from "@/providers/ActiveProfileProvider";
import { UpsellModal } from "./UpsellModal";
import {
  AiChatIllustration,
  MarketIntelligenceIllustration,
  NoViewsIllustration,
  SearchProfileLimitIllustration,
} from "./upsell-illustrations";
import { planDisplayName } from "@/lib/format";

dayjs.locale("es");

/**
 * Se acabaron las vistas. Lo dispara el fallo `NO_VIEWS_LEFT` del
 * desbloqueo, así que aquí no se decide nada: solo se explica y se ofrece salida.
 */
export function NoViewsLeftModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const { data: quota } = useViewQuota();
  const upgrade = useUpgradePlan(open);

  // La fecha la manda el backend: cada usuario recarga en su aniversario y el
  // corte va en TZ de Perú, no en la del navegador. Sin fecha, el plan no recarga
  // nunca (FREE) y prometer una renovación sería mentir.
  const renewal = quota?.resetsAt
    ? dayjs(quota.resetsAt).format("D [de] MMMM")
    : null;

  return (
    <UpsellModal
      open={open}
      onOpenChange={onOpenChange}
      tone="amber"
      badge="Sin vistas disponibles"
      illustration={<NoViewsIllustration />}
      title={
        !quota?.limit
          ? "Te quedaste sin vistas de licitación"
          : renewal
            ? `Usaste tus ${quota.limit} vistas de este mes`
            : `Usaste tus ${quota.limit} vistas gratuitas`
      }
      description={
        <>
          {renewal
            ? `Se renuevan el ${renewal}.`
            : "Son las vistas de tu plan gratuito y no se recargan."}
          {/* Con renovación el upgrade es la alternativa a esperar ("o"); sin
              ella no hay nada que esperar, así que es la única salida. */}
          {upgrade?.plan_monthlyviews != null &&
            `${renewal ? " O pasa" : " Pasa"} al plan ${planDisplayName(upgrade)} y revisa hasta ${upgrade.plan_monthlyviews} oportunidades al mes.`}
        </>
      }
      primaryLabel={
        upgrade
          ? `Subir al plan ${planDisplayName(upgrade)} →`
          : "Ver planes →"
      }
      onPrimary={() => {
        capture("upsell_cta_clicked", { kind: "no_views" });
        onOpenChange(false);
        navigate("/settings/plan");
      }}
      // Sin recarga no hay renovación que esperar: la única salida es cerrar.
      secondaryLabel={renewal ? "Esperar a mi renovación" : "Ahora no"}
    />
  );
}

/**
 * El plan no admite otra línea de negocio. Lo dispara `SEARCH_PROFILE_LIMIT` al
 * crear, tanto desde la barra lateral como desde el wizard de introducción.
 */
export function SearchProfileLimitModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const { profiles } = useActiveProfile();
  const { data: subscription } = useMySubscription(open);
  const upgrade = useUpgradePlan(open);

  // Si la suscripción aún no llegó, lo que tiene ES el tope: el backend acaba de
  // rechazar la siguiente.
  const limit = subscription?.maxSearchProfiles ?? profiles.length;

  return (
    <UpsellModal
      open={open}
      onOpenChange={onOpenChange}
      tone="blue"
      badge={`${profiles.length} de ${limit} ${limit === 1 ? "línea" : "líneas"} en uso`}
      illustration={<SearchProfileLimitIllustration />}
      title="Alcanzaste tus líneas de negocio"
      description={
        <>
          {`Tu plan permite ${limit} ${limit === 1 ? "línea" : "líneas"} en paralelo.`}
          {upgrade?.plan_maxsearchprofiles != null &&
            ` Con el plan ${planDisplayName(upgrade)} rastreas hasta ${upgrade.plan_maxsearchprofiles} rubros distintos a la vez.`}
        </>
      }
      primaryLabel={
        upgrade?.plan_maxsearchprofiles != null
          ? `Ampliar a ${upgrade.plan_maxsearchprofiles} líneas →`
          : "Ver planes →"
      }
      onPrimary={() => {
        capture("upsell_cta_clicked", { kind: "search_profile_limit" });
        onOpenChange(false);
        navigate("/settings/plan");
      }}
      secondaryLabel="Editar mis líneas actuales"
    />
  );
}

const MARKET_INTELLIGENCE_SEEN_KEY = "licitigo_market_intelligence_seen";

/**
 * Anuncia que Inteligencia de mercado está abierta a todos durante la campaña.
 * No bloquea nada: se ve una vez al entrar a Empresas y deja de aparecer cuando
 * la fecha pasa. Cerrar la campaña es manual (ver `constants.ts`).
 */
export function MarketIntelligenceModal() {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(
    () =>
      Date.now() > MARKET_INTELLIGENCE_FREE_ENDS_AT.getTime() ||
      localStorage.getItem(MARKET_INTELLIGENCE_SEEN_KEY) != null,
  );
  const upgrade = useUpgradePlan();

  // DESMONTADO a propósito: hoy no se renderiza en ningún lado. Su CTA compartía
  // `upsell_cta_clicked` con los upsells de verdad, y ese evento viaja a Meta como
  // `AddToCart`; cerrar un modal que salió solo no es intención de compra y ensucia
  // la señal. Para revivirlo, dale su propio nombre de evento antes de montarlo.

  // Sin plan por encima del suyo la función ya es parte de lo que paga: anunciarle
  // que "es gratis hasta octubre" le vendería algo que ya tiene.
  if (!upgrade) return null;

  function close() {
    localStorage.setItem(MARKET_INTELLIGENCE_SEEN_KEY, "1");
    setDismissed(true);
  }

  return (
    <UpsellModal
      open={!dismissed}
      onOpenChange={(next) => !next && close()}
      tone="emerald"
      badge={`Gratis hasta el ${MARKET_INTELLIGENCE_FREE_UNTIL}`}
      illustration={<MarketIntelligenceIllustration />}
      title="Inteligencia de mercado"
      description={`Mira quién gana en tu rubro, a qué precios y con qué entidades. Es una función del plan ${planDisplayName(upgrade)} y hasta el ${MARKET_INTELLIGENCE_FREE_UNTIL} la tienes abierta, sin costo.`}
      primaryLabel="Explorar sin costo →"
      onPrimary={() => {
        capture("upsell_cta_clicked", { kind: "market_intelligence" });
        close();
      }}
      secondaryLabel={`Ver qué incluye ${planDisplayName(upgrade)}`}
      onSecondary={() => {
        close();
        navigate("/settings/plan");
      }}
    />
  );
}

/**
 * Chat con IA y conexión por MCP: aún no existe como producto y no está en la
 * tabla de precios, así que la salida es hablar con ventas, no contratar.
 */
export function AiChatModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <UpsellModal
      open={open}
      onOpenChange={onOpenChange}
      tone="violet"
      badge="Plan Corporativo"
      illustration={<AiChatIllustration />}
      title="Pregúntale a tus licitaciones"
      description="Chatea con tus licitaciones y conecta LicitiGo a ChatGPT o Claude vía MCP. Disponible en el plan Corporativo, a medida para tu operación."
      primaryLabel="Hablar con ventas →"
      onPrimary={() => {
        capture("upsell_cta_clicked", { kind: "ai_chat" });
        onOpenChange(false);
        window.location.href = `mailto:${SALES_EMAIL}?subject=${encodeURIComponent("Quiero saber del plan Corporativo")}`;
      }}
      secondaryLabel="Ahora no"
    >
      <div className="flex items-center gap-2.5 rounded-xl border border-[#E9EDF6] bg-[#F6F8FC] px-4 py-3.5">
        <span className="size-[7px] shrink-0 rounded-full bg-[#8B5CF6]" />
        <p className="text-[13px] leading-[1.45] text-[#5B6472]">
          “Resúmeme las oportunidades de acero en el norte este mes.”
        </p>
      </div>
    </UpsellModal>
  );
}
